import { FC, useEffect } from 'react'

import { useLocation, useParams } from 'react-router'

import { Network } from '@tribeplatform/gql-client/types'
import { useI18n } from '@tribeplatform/react-components/i18n'
import { useSpaceUtil } from '@tribeplatform/react-sdk/hooks'
import { SlateRendererProps } from '@tribeplatform/react-slate-kit/components'

import { useCurrentPage } from '../hooks/useCurrentPage'
import { useQuery } from '../lib/useQuery'
import { useMobileSidebar } from '../MobileSidebarProvider'
import { NotFound } from './Error/NotFound'
import { Layout } from './Layout'
import { SlateLayout } from './Layout/layout.enum'
import { PageMeta } from './PageMeta'
import { Space } from './Space/Space'

export const SpaceRoutes: FC<{
  network: Network
}> = ({ network }) => {
  const { $t } = useI18n()
  const { closeMobileSidebar } = useMobileSidebar()
  const queryParams = useQuery()
  const urlParams = useParams()
  const location = useLocation()
  const { pathname } = location
  const { generatePath, matchPath } = useSpaceUtil()
  const { data: page, error } = useCurrentPage({
    useQueryOptions: {
      retry: 0,
    },
  })

  useEffect(() => {
    closeMobileSidebar()
  }, [closeMobileSidebar, location])

  let context: SlateRendererProps['context']
  if (page?.space) {
    const { params = null } = matchPath(page?.space, pathname) || {}
    context = {
      path: generatePath(page?.space, params),
      urlParams: params || {},
      queryParams,
    }
  } else {
    context = {
      path: pathname,
      urlParams,
      queryParams,
    }
  }

  if (!page?.space && !!error) {
    return (
      <Layout
        layout={page?.space?.layout as SlateLayout}
        navigationSlates={network?.navigationSlates}
        context={context}
      >
        <NotFound
          title={$t({
            defaultMessage: '{SPACE_CC} not found',
            id: 'Space.NotFound',
          })}
          description={$t({
            defaultMessage:
              'This {SPACE} does not exist, is removed, or you don’t have permission to access it.',
            id: 'Space.NotFound.Description',
          })}
        />
      </Layout>
    )
  }

  return (
    <>
      <PageMeta page={page} />
      <Space network={network} page={page} context={context} />
    </>
  )
}
